import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Shield, Key, Users, Gamepad2, LogOut, Plus, Trash2, Copy,
  RotateCcw, Calendar, Check, X, Download, ChevronDown, ChevronUp, ExternalLink
} from "lucide-react";

export default function SuperadminDashboard() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [users, setUsers] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  // Code creation form
  const [codeType, setCodeType] = useState("registration");
  const [codeRole, setCodeRole] = useState("student");
  const [maxUses, setMaxUses] = useState(1);
  const [expiresAt, setExpiresAt] = useState("");
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);

  // Bulk generation
  const [bulkCount, setBulkCount] = useState(10);
  const [bulkType, setBulkType] = useState("registration");
  const [bulkRole, setBulkRole] = useState("student");
  const [bulkExpiry, setBulkExpiry] = useState("");
  const [bulkNote, setBulkNote] = useState("");
  const [bulkGenerating, setBulkGenerating] = useState(false);
  const [expandedCode, setExpandedCode] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  // Multi-select state
  const [selectedCodes, setSelectedCodes] = useState(new Set());
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [selectedGames, setSelectedGames] = useState(new Set());

  // Game creation form
  const [gameName, setGameName] = useState("");
  const [gameInstructorId, setGameInstructorId] = useState("");
  const [gameMaxTeams, setGameMaxTeams] = useState(8);
  const [gameQuarters, setGameQuarters] = useState(8);
  const [creatingGame, setCreatingGame] = useState(false);
  const [autoAdvance, setAutoAdvance] = useState(false);
  const [scheduleTz, setScheduleTz] = useState("UTC");
  const [quarterSchedule, setQuarterSchedule] = useState([]);

  // Schedule editing
  const [editScheduleGameId, setEditScheduleGameId] = useState(null);
  const [editSchedule, setEditSchedule] = useState([]);
  const [editTz, setEditTz] = useState("UTC");
  const [savingSchedule, setSavingSchedule] = useState(false);

  // Inline quarters editing
  const [editingQuartersGameId, setEditingQuartersGameId] = useState(null);
  const [editingQuartersValue, setEditingQuartersValue] = useState(0);

  const saveQuartersCount = async (gameId) => {
    try {
      await axios.patch(`${API}/admin/games/${gameId}/quarters`, { quarters_count: editingQuartersValue });
      toast.success(`Quarters updated to ${editingQuartersValue}`);
      setEditingQuartersGameId(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update quarters");
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [codesRes, usersRes, gamesRes] = await Promise.all([
        axios.get(`${API}/admin/codes`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/games`),
      ]);
      setCodes(codesRes.data);
      setUsers(usersRes.data);
      setGames(gamesRes.data);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCode = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const payload = {
        code_type: codeType,
        role: codeType === "registration" ? codeRole : null,
        max_uses: maxUses,
        expires_at: expiresAt ? new Date(expiresAt).toISOString() : null,
        note: note || null,
      };
      await axios.post(`${API}/admin/codes`, payload);
      toast.success("Code created");
      setNote("");
      setMaxUses(1);
      setExpiresAt("");
      fetchAll();
    } catch (e) {
      toast.error("Failed to create code");
    } finally {
      setCreating(false);
    }
  };

  const handleBulkGenerate = async (e) => {
    e.preventDefault();
    setBulkGenerating(true);
    try {
      const payload = {
        code_type: bulkType,
        role: bulkType === "registration" ? bulkRole : null,
        count: bulkCount,
        expires_at: bulkExpiry ? new Date(bulkExpiry).toISOString() : null,
        note: bulkNote || null,
      };
      const res = await axios.post(`${API}/admin/codes/bulk`, payload);
      const generated = res.data;
      toast.success(`${generated.length} codes generated`);

      // Auto-download CSV
      const header = "Code,Type,Role,Expires\n";
      const rows = generated.map(c => `${c.code},${c.code_type},${c.role || "any"},${c.expires_at || "never"}`).join("\n");
      const blob = new Blob([header + rows], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `skyboard_codes_${bulkType}_${new Date().toISOString().slice(0,10)}.csv`;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);

      setBulkNote("");
      setBulkExpiry("");
      fetchAll();
    } catch (e) {
      toast.error("Failed to generate codes");
    } finally {
      setBulkGenerating(false);
    }
  };

  const deleteCode = async (id) => {
    setConfirmAction({ type: "deleteCode", id, label: t("admin.code") });
  };

  const deleteUser = async (id, email) => {
    setConfirmAction({ type: "deleteUser", id, label: email });
  };

  const resetPassword = async (id, email) => {
    setConfirmAction({ type: "resetPassword", id, label: email });
  };

  const deleteGame = async (id, name) => {
    setConfirmAction({ type: "deleteGame", id, label: name });
  };

  const executeConfirm = async () => {
    if (!confirmAction) return;
    const { type, id, ids } = confirmAction;
    try {
      if (type === "deleteCode") {
        await axios.delete(`${API}/admin/codes/${id}`);
        toast.success("Code deleted");
      } else if (type === "deleteUser") {
        await axios.delete(`${API}/admin/users/${id}`);
        toast.success("User deleted");
      } else if (type === "resetPassword") {
        const res = await axios.put(`${API}/admin/users/${id}/reset-password`);
        toast.success(`New password: ${res.data.new_password}`, { duration: 15000 });
      } else if (type === "deleteGame") {
        await axios.delete(`${API}/admin/games/${id}`);
        toast.success("Game deleted");
      } else if (type === "bulkDeleteCodes") {
        const res = await axios.post(`${API}/admin/codes/bulk-delete`, { ids: [...ids] });
        toast.success(`${res.data.deleted} codes deleted`);
        setSelectedCodes(new Set());
      } else if (type === "bulkDeleteUsers") {
        const res = await axios.post(`${API}/admin/users/bulk-delete`, { ids: [...ids] });
        toast.success(`${res.data.deleted} users deleted`);
        setSelectedUsers(new Set());
      } else if (type === "bulkDeleteGames") {
        const res = await axios.post(`${API}/admin/games/bulk-delete`, { ids: [...ids] });
        toast.success(`${res.data.deleted} games deleted`);
        setSelectedGames(new Set());
      }
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
    setConfirmAction(null);
  };

  // Selection helpers
  const toggleSelect = (set, setFn, id) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setFn(next);
  };
  const toggleSelectAll = (set, setFn, allIds) => {
    setFn(set.size === allIds.length ? new Set() : new Set(allIds));
  };
  const bulkDeleteCodes = () => {
    if (selectedCodes.size === 0) return;
    setConfirmAction({ type: "bulkDeleteCodes", ids: selectedCodes, label: `${selectedCodes.size} codes` });
  };
  const bulkDeleteUsers = () => {
    if (selectedUsers.size === 0) return;
    setConfirmAction({ type: "bulkDeleteUsers", ids: selectedUsers, label: `${selectedUsers.size} users` });
  };
  const bulkDeleteGames = () => {
    if (selectedGames.size === 0) return;
    setConfirmAction({ type: "bulkDeleteGames", ids: selectedGames, label: `${selectedGames.size} games` });
  };

  // Build schedule when quarters count or autoAdvance changes
  const buildEmptySchedule = (count) => {
    return Array.from({ length: count }, (_, i) => ({
      quarter: i + 1,
      scheduled_at: "",
      advanced: false,
    }));
  };

  const handleQuartersChange = (val) => {
    const q = parseInt(val) || 8;
    setGameQuarters(q);
    if (autoAdvance) setQuarterSchedule(buildEmptySchedule(q));
  };

  const handleAutoAdvanceToggle = (enabled) => {
    setAutoAdvance(enabled);
    if (enabled) setQuarterSchedule(buildEmptySchedule(gameQuarters));
    else setQuarterSchedule([]);
  };

  const updateScheduleItem = (index, dateTimeStr) => {
    setQuarterSchedule(prev => prev.map((item, i) =>
      i === index ? { ...item, scheduled_at: dateTimeStr ? new Date(dateTimeStr).toISOString() : "" } : item
    ));
  };

  const handleCreateGame = async (e) => {
    e.preventDefault();
    if (!gameInstructorId) { toast.error(t("admin.selectInstructor")); return; }
    if (autoAdvance) {
      const hasEmpty = quarterSchedule.some(s => !s.scheduled_at);
      if (hasEmpty) { toast.error("Please set date/time for all quarters"); return; }
    }
    setCreatingGame(true);
    try {
      await axios.post(`${API}/games`, {
        name: gameName,
        instructor_id: gameInstructorId,
        max_teams: gameMaxTeams,
        quarters_count: gameQuarters,
        regions: ["europe"],
        auto_advance: autoAdvance,
        schedule_timezone: autoAdvance ? scheduleTz : null,
        quarter_schedule: autoAdvance ? quarterSchedule : null,
      });
      toast.success(t("admin.gameCreated"));
      setGameName("");
      setGameInstructorId("");
      setGameMaxTeams(8);
      setGameQuarters(8);
      setAutoAdvance(false);
      setQuarterSchedule([]);
      setScheduleTz("UTC");
      fetchAll();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to create game");
    } finally {
      setCreatingGame(false);
    }
  };

  const openEditSchedule = async (game) => {
    setEditScheduleGameId(game.id);
    setEditTz(game.schedule_timezone || "UTC");
    if (game.quarter_schedule) {
      setEditSchedule(game.quarter_schedule.map(s => ({ ...s })));
    } else {
      setEditSchedule(buildEmptySchedule(game.quarters_count));
    }
  };

  const updateEditScheduleItem = (index, dateTimeStr) => {
    setEditSchedule(prev => prev.map((item, i) =>
      i === index ? { ...item, scheduled_at: dateTimeStr ? new Date(dateTimeStr).toISOString() : "" } : item
    ));
  };

  const saveSchedule = async () => {
    const hasEmpty = editSchedule.some(s => !s.scheduled_at);
    if (hasEmpty) { toast.error("Please set date/time for all quarters"); return; }
    setSavingSchedule(true);
    try {
      await axios.put(`${API}/games/${editScheduleGameId}/schedule`, {
        auto_advance: true,
        schedule_timezone: editTz,
        quarter_schedule: editSchedule,
      });
      toast.success("Schedule saved");
      setEditScheduleGameId(null);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save schedule");
    } finally {
      setSavingSchedule(false);
    }
  };

  const TIMEZONES = [
    "UTC", "US/Eastern", "US/Central", "US/Pacific",
    "Europe/London", "Europe/Berlin", "Europe/Madrid", "Europe/Paris",
    "Asia/Tokyo", "Asia/Shanghai", "Asia/Kolkata",
    "Australia/Sydney", "America/Sao_Paulo", "America/Mexico_City",
  ];

  const toLocalInput = (isoStr) => {
    if (!isoStr) return "";
    const d = new Date(isoStr);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const copyCode = (code) => {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = code;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      toast.success(`Copied: ${code}`);
    } catch {
      toast.info(code, { duration: 8000, description: "Select and copy manually" });
    }
  };

  const isExpired = (exAt) => exAt && new Date(exAt) < new Date();
  const isFullyUsed = (c) => c.uses >= c.max_uses;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-slate-700 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src="/skyboard-logo.png" alt="SkyBoard" className="h-10 w-auto" />
            <div>
              <h1 className="text-lg font-heading font-bold text-white">SKYBOARD ADMIN</h1>
              <p className="text-xs text-slate-400">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-300">
            <span data-testid="admin-stats">{codes.length} {t("admin.codes")} | {users.length} {t("admin.users")} | {games.length} {t("admin.games")}</span>
            <Button variant="ghost" size="sm" onClick={() => { logout(); navigate("/login"); }} className="text-slate-400 hover:text-white" data-testid="admin-logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs defaultValue="codes" className="space-y-6">
          <TabsList className="bg-slate-800 border border-slate-700">
            <TabsTrigger value="codes" className="gap-2 data-[state=active]:bg-amber-600 data-[state=active]:text-white" data-testid="admin-tab-codes">
              <Key className="w-4 h-4" /> {t("admin.codes")}
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2 data-[state=active]:bg-amber-600 data-[state=active]:text-white" data-testid="admin-tab-users">
              <Users className="w-4 h-4" /> {t("admin.users")}
            </TabsTrigger>
            <TabsTrigger value="games" className="gap-2 data-[state=active]:bg-amber-600 data-[state=active]:text-white" data-testid="admin-tab-games">
              <Gamepad2 className="w-4 h-4" /> {t("admin.games")}
            </TabsTrigger>
          </TabsList>

          {/* === CODES TAB === */}
          <TabsContent value="codes" className="space-y-6">
            {/* Create Code Form */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2"><Plus className="w-5 h-5 text-amber-500" /> {t("admin.createCode")}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateCode} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <Label className="text-slate-300 text-xs">{t("admin.codeType")}</Label>
                    <Select value={codeType} onValueChange={setCodeType}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="code-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="registration">{t("admin.registration")}</SelectItem>
                        <SelectItem value="game_creation">{t("admin.gameCreation")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {codeType === "registration" && (
                    <div>
                      <Label className="text-slate-300 text-xs">{t("admin.role")}</Label>
                      <Select value={codeRole} onValueChange={setCodeRole}>
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="code-role-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="student">{t("auth.student")}</SelectItem>
                          <SelectItem value="instructor">{t("auth.instructor")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label className="text-slate-300 text-xs">{t("admin.maxUses")}</Label>
                    <Input type="number" min={1} max={10000} value={maxUses} onChange={e => setMaxUses(parseInt(e.target.value) || 1)} className="bg-slate-700 border-slate-600 text-white" data-testid="code-max-uses-input" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">{t("admin.expiresAt")}</Label>
                    <Input type="datetime-local" value={expiresAt} onChange={e => setExpiresAt(e.target.value)} className="bg-slate-700 border-slate-600 text-white" data-testid="code-expires-input" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label className="text-slate-300 text-xs">{t("admin.note")}</Label>
                    <Input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. MBA Section A students" className="bg-slate-700 border-slate-600 text-white" data-testid="code-note-input" />
                  </div>
                  <div className="flex items-end">
                    <Button type="submit" disabled={creating} className="bg-amber-600 hover:bg-amber-700 w-full" data-testid="create-code-btn">
                      <Plus className="w-4 h-4 mr-2" /> {creating ? "..." : t("admin.createCode")}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Bulk Generation */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2"><Copy className="w-5 h-5 text-amber-500" /> {t("admin.bulkGenerate")}</CardTitle>
                <CardDescription className="text-slate-400">{t("admin.bulkDesc")}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleBulkGenerate} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div>
                    <Label className="text-slate-300 text-xs">{t("admin.count")}</Label>
                    <Input type="number" min={1} max={500} value={bulkCount} onChange={e => setBulkCount(parseInt(e.target.value) || 1)} className="bg-slate-700 border-slate-600 text-white font-mono" data-testid="bulk-count-input" />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">{t("admin.codeType")}</Label>
                    <Select value={bulkType} onValueChange={setBulkType}>
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="bulk-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="registration">{t("admin.registration")}</SelectItem>
                        <SelectItem value="game_creation">{t("admin.gameCreation")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {bulkType === "registration" && (
                    <div>
                      <Label className="text-slate-300 text-xs">{t("admin.role")}</Label>
                      <Select value={bulkRole} onValueChange={setBulkRole}>
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="bulk-role-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="student">{t("auth.student")}</SelectItem>
                          <SelectItem value="instructor">{t("auth.instructor")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div>
                    <Label className="text-slate-300 text-xs">{t("admin.expiresAt")}</Label>
                    <Input type="datetime-local" value={bulkExpiry} onChange={e => setBulkExpiry(e.target.value)} className="bg-slate-700 border-slate-600 text-white" data-testid="bulk-expires-input" />
                  </div>
                  <div className="flex items-end">
                    <Button type="submit" disabled={bulkGenerating} className="bg-emerald-600 hover:bg-emerald-700 w-full gap-2" data-testid="bulk-generate-btn">
                      <Download className="w-4 h-4" /> {bulkGenerating ? "..." : t("admin.generateAndDownload")}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Codes List */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">{t("admin.allCodes")} ({codes.length})</CardTitle>
                {selectedCodes.size > 0 && (
                  <Button size="sm" onClick={bulkDeleteCodes} className="bg-red-600 hover:bg-red-700 gap-2 text-xs" data-testid="bulk-delete-codes-btn">
                    <Trash2 className="w-3.5 h-3.5" /> Delete {selectedCodes.size} selected
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 border-b border-slate-700">
                        <th className="py-3 px-2 w-8">
                          <input type="checkbox" className="accent-amber-500 cursor-pointer" checked={codes.length > 0 && selectedCodes.size === codes.length} onChange={() => toggleSelectAll(selectedCodes, setSelectedCodes, codes.map(c => c.id))} data-testid="select-all-codes" />
                        </th>
                        <th className="text-left py-3 px-2">{t("admin.code")}</th>
                        <th className="text-left py-3 px-2">{t("admin.type")}</th>
                        <th className="text-left py-3 px-2">{t("admin.role")}</th>
                        <th className="text-center py-3 px-2">{t("admin.usage")}</th>
                        <th className="text-left py-3 px-2">{t("admin.status")}</th>
                        <th className="text-left py-3 px-2">{t("admin.note")}</th>
                        <th className="text-right py-3 px-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {codes.map(c => {
                        const expired = isExpired(c.expires_at);
                        const used = isFullyUsed(c);
                        const active = !expired && !used;
                        const hasUsedBy = c.used_by && c.used_by.length > 0;
                        const isExpanded = expandedCode === c.id;
                        return (
                          <React.Fragment key={c.id}>
                            <tr className={`border-b border-slate-700/50 hover:bg-slate-700/30 ${hasUsedBy ? "cursor-pointer" : ""} ${selectedCodes.has(c.id) ? "bg-amber-900/20" : ""}`} onClick={() => hasUsedBy && setExpandedCode(isExpanded ? null : c.id)}>
                              <td className="py-3 px-2" onClick={e => e.stopPropagation()}>
                                <input type="checkbox" className="accent-amber-500 cursor-pointer" checked={selectedCodes.has(c.id)} onChange={() => toggleSelect(selectedCodes, setSelectedCodes, c.id)} data-testid={`select-code-${c.id}`} />
                              </td>
                              <td className="py-3 px-2">
                                <button onClick={(e) => { e.stopPropagation(); copyCode(c.code); }} className="font-mono text-amber-400 hover:text-amber-300 flex items-center gap-1" data-testid={`copy-code-${c.code}`}>
                                  {c.code} <Copy className="w-3 h-3" />
                                </button>
                              </td>
                              <td className="py-3 px-2 text-slate-300">{c.code_type === "registration" ? t("admin.registration") : t("admin.gameCreation")}</td>
                              <td className="py-3 px-2 text-slate-300">{c.role || "—"}</td>
                              <td className="py-3 px-2 text-center text-slate-300 font-mono">
                                <span className="flex items-center justify-center gap-1">
                                  {c.uses}/{c.max_uses}
                                  {hasUsedBy && (isExpanded ? <ChevronUp className="w-3 h-3 text-amber-400" /> : <ChevronDown className="w-3 h-3 text-amber-400" />)}
                                </span>
                              </td>
                              <td className="py-3 px-2">
                                {active ? (
                                  <span className="flex items-center gap-1 text-emerald-400 text-xs"><Check className="w-3 h-3" /> {t("admin.active")}</span>
                                ) : expired ? (
                                  <span className="flex items-center gap-1 text-red-400 text-xs"><Calendar className="w-3 h-3" /> {t("admin.expired")}</span>
                                ) : (
                                  <span className="flex items-center gap-1 text-slate-500 text-xs"><X className="w-3 h-3" /> {t("admin.used")}</span>
                                )}
                              </td>
                              <td className="py-3 px-2 text-slate-400 text-xs max-w-[200px] truncate">{c.note || "—"}</td>
                              <td className="py-3 px-2 text-right">
                                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); deleteCode(c.id); }} className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-8 w-8 p-0" data-testid={`delete-code-${c.id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                            {isExpanded && hasUsedBy && (
                              <tr className="bg-slate-750">
                                <td colSpan={8} className="px-6 py-3">
                                  <p className="text-xs text-slate-400 mb-2 font-semibold">{t("admin.usedBy")}:</p>
                                  <div className="space-y-1.5">
                                    {c.used_by.map((u) => (
                                      <div key={u.email || u.name} className="flex items-center gap-3 text-xs bg-slate-800 rounded px-3 py-2 border border-slate-700">
                                        <span className="text-white font-medium">{u.name || u.email}</span>
                                        <span className="text-slate-400">{u.email}</span>
                                        {u.role && <span className={`px-1.5 py-0.5 rounded text-[10px] ${u.role === "instructor" ? "bg-sky-900/50 text-sky-300" : "bg-emerald-900/50 text-emerald-300"}`}>{u.role}</span>}
                                        {u.game && <span className="text-amber-400">{u.game}</span>}
                                        <span className="text-slate-500 ml-auto">{new Date(u.at).toLocaleString()}</span>
                                      </div>
                                    ))}
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                  {codes.length === 0 && <p className="text-slate-500 text-center py-8">{t("admin.noCodes")}</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* === USERS TAB === */}
          <TabsContent value="users">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">{t("admin.allUsers")} ({users.length})</CardTitle>
                {selectedUsers.size > 0 && (
                  <Button size="sm" onClick={bulkDeleteUsers} className="bg-red-600 hover:bg-red-700 gap-2 text-xs" data-testid="bulk-delete-users-btn">
                    <Trash2 className="w-3.5 h-3.5" /> Delete {selectedUsers.size} selected
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 border-b border-slate-700">
                        <th className="py-3 px-2 w-8">
                          <input type="checkbox" className="accent-amber-500 cursor-pointer" checked={users.length > 0 && selectedUsers.size === users.length} onChange={() => toggleSelectAll(selectedUsers, setSelectedUsers, users.map(u => u.id))} data-testid="select-all-users" />
                        </th>
                        <th className="text-left py-3 px-2">{t("auth.name")}</th>
                        <th className="text-left py-3 px-2">{t("auth.email")}</th>
                        <th className="text-left py-3 px-2">{t("admin.role")}</th>
                        <th className="text-left py-3 px-2">{t("admin.created")}</th>
                        <th className="text-right py-3 px-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map(u => (
                        <tr key={u.id} className={`border-b border-slate-700/50 hover:bg-slate-700/30 ${selectedUsers.has(u.id) ? "bg-amber-900/20" : ""}`}>
                          <td className="py-3 px-2">
                            <input type="checkbox" className="accent-amber-500 cursor-pointer" checked={selectedUsers.has(u.id)} onChange={() => toggleSelect(selectedUsers, setSelectedUsers, u.id)} data-testid={`select-user-${u.id}`} />
                          </td>
                          <td className="py-3 px-2 text-white font-medium">{u.name}</td>
                          <td className="py-3 px-2 text-slate-300">{u.email}</td>
                          <td className="py-3 px-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${u.role === "instructor" ? "bg-sky-900/50 text-sky-300" : "bg-emerald-900/50 text-emerald-300"}`}>
                              {u.role}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-slate-400 text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                          <td className="py-3 px-2 text-right flex gap-1 justify-end">
                            <Button size="sm" variant="ghost" onClick={() => resetPassword(u.id, u.email)} className="text-amber-400 hover:text-amber-300 hover:bg-amber-900/20 h-8 w-8 p-0" title={t("admin.resetPassword")} data-testid={`reset-pw-${u.id}`}>
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => deleteUser(u.id, u.email)} className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-8 w-8 p-0" title={t("admin.deleteUser")} data-testid={`delete-user-${u.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {users.length === 0 && <p className="text-slate-500 text-center py-8">{t("admin.noUsers")}</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* === GAMES TAB === */}
          <TabsContent value="games" className="space-y-6">
            {/* Create Game Form */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2"><Plus className="w-5 h-5 text-amber-500" /> {t("admin.createGame")}</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleCreateGame} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <Label className="text-slate-300 text-xs">{t("instructor.gameName")}</Label>
                      <Input value={gameName} onChange={e => setGameName(e.target.value)} required placeholder="MBA Simulation Q1" className="bg-slate-700 border-slate-600 text-white" data-testid="admin-game-name-input" />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-xs">{t("auth.instructor")}</Label>
                      <Select value={gameInstructorId} onValueChange={setGameInstructorId}>
                        <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="admin-game-instructor-select"><SelectValue placeholder={t("admin.selectInstructor")} /></SelectTrigger>
                        <SelectContent>
                          {users.filter(u => u.role === "instructor").map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-300 text-xs">{t("admin.maxTeams")}</Label>
                      <Input type="number" min={2} max={8} value={gameMaxTeams} onChange={e => setGameMaxTeams(parseInt(e.target.value) || 8)} className="bg-slate-700 border-slate-600 text-white font-mono" data-testid="admin-game-teams-input" />
                    </div>
                    <div>
                      <Label className="text-slate-300 text-xs">{t("admin.quarters")}</Label>
                      <Input type="number" min={4} max={20} value={gameQuarters} onChange={e => handleQuartersChange(e.target.value)} className="bg-slate-700 border-slate-600 text-white font-mono" data-testid="admin-game-quarters-input" />
                    </div>
                  </div>

                  {/* Auto-advance toggle */}
                  <div className="flex items-center gap-3 pt-2">
                    <button type="button" onClick={() => handleAutoAdvanceToggle(!autoAdvance)}
                      className={`relative w-11 h-6 rounded-full transition-colors ${autoAdvance ? "bg-amber-500" : "bg-slate-600"}`}
                      data-testid="auto-advance-toggle"
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${autoAdvance ? "translate-x-5" : ""}`} />
                    </button>
                    <div>
                      <span className="text-sm text-white font-medium">Auto-advance quarters</span>
                      <p className="text-xs text-slate-400">Set a specific date & time for each quarter to advance automatically</p>
                    </div>
                  </div>

                  {/* Schedule configuration */}
                  {autoAdvance && (
                    <div className="bg-slate-900/50 rounded-lg p-4 space-y-3 border border-slate-700">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-white flex items-center gap-2"><Calendar className="w-4 h-4 text-amber-500" /> Quarter Schedule</h4>
                        <div className="w-48">
                          <Select value={scheduleTz} onValueChange={setScheduleTz}>
                            <SelectTrigger className="bg-slate-700 border-slate-600 text-white text-xs h-8" data-testid="schedule-tz-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                        {quarterSchedule.map((qs, i) => (
                          <div key={qs.quarter} className="flex items-center gap-2">
                            <span className="text-xs text-slate-400 w-7 shrink-0">Q{qs.quarter}</span>
                            <input
                              type="datetime-local"
                              value={toLocalInput(qs.scheduled_at)}
                              onChange={e => updateScheduleItem(i, e.target.value)}
                              className="flex-1 bg-slate-700 border border-slate-600 text-white text-xs rounded px-2 py-1.5"
                              data-testid={`schedule-q${qs.quarter}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div>
                    <Button type="submit" disabled={creatingGame} className="bg-amber-600 hover:bg-amber-700 gap-2" data-testid="admin-create-game-btn">
                      <Gamepad2 className="w-4 h-4" /> {creatingGame ? "..." : t("admin.createGame")}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Games List */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white">{t("admin.allGames")} ({games.length})</CardTitle>
                {selectedGames.size > 0 && (
                  <Button size="sm" onClick={bulkDeleteGames} className="bg-red-600 hover:bg-red-700 gap-2 text-xs" data-testid="bulk-delete-games-btn">
                    <Trash2 className="w-3.5 h-3.5" /> Delete {selectedGames.size} selected
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-slate-400 border-b border-slate-700">
                        <th className="py-3 px-2 w-8">
                          <input type="checkbox" className="accent-amber-500 cursor-pointer" checked={games.length > 0 && selectedGames.size === games.length} onChange={() => toggleSelectAll(selectedGames, setSelectedGames, games.map(g => g.id))} data-testid="select-all-games" />
                        </th>
                        <th className="text-left py-3 px-2">{t("instructor.gameName")}</th>
                        <th className="text-left py-3 px-2">{t("auth.instructor")}</th>
                        <th className="text-center py-3 px-2">{t("admin.teams")}</th>
                        <th className="text-center py-3 px-2">{t("admin.quarter")}</th>
                        <th className="text-left py-3 px-2">{t("admin.status")}</th>
                        <th className="text-right py-3 px-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {games.map(g => (
                        <tr key={g.id} className={`border-b border-slate-700/50 hover:bg-slate-700/30 ${selectedGames.has(g.id) ? "bg-amber-900/20" : ""}`}>
                          <td className="py-3 px-2">
                            <input type="checkbox" className="accent-amber-500 cursor-pointer" checked={selectedGames.has(g.id)} onChange={() => toggleSelect(selectedGames, setSelectedGames, g.id)} data-testid={`select-game-${g.id}`} />
                          </td>
                          <td className="py-3 px-2">
                            <span className="text-white font-medium">{g.name}</span>
                            {g.auto_advance && (
                              <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-900/50 text-amber-300">AUTO</span>
                            )}
                          </td>
                          <td className="py-3 px-2 text-slate-300 text-xs">{g.instructor_name}<br/><span className="text-slate-500">{g.instructor_email}</span></td>
                          <td className="py-3 px-2 text-center text-slate-300 font-mono">{g.team_count}/{g.max_teams}</td>
                          <td className="py-3 px-2 text-center text-slate-300 font-mono">
                            {editingQuartersGameId === g.id ? (
                              <span className="inline-flex items-center gap-1">
                                <span>Q{g.current_quarter}/</span>
                                <input
                                  type="number"
                                  min={Math.max(g.current_quarter, 1)}
                                  max={24}
                                  value={editingQuartersValue}
                                  onChange={e => setEditingQuartersValue(parseInt(e.target.value) || 1)}
                                  onKeyDown={e => { if (e.key === "Enter") saveQuartersCount(g.id); if (e.key === "Escape") setEditingQuartersGameId(null); }}
                                  className="w-12 bg-slate-700 border border-amber-500 rounded px-1 py-0.5 text-center text-white text-sm"
                                  autoFocus
                                  data-testid={`edit-quarters-input-${g.id}`}
                                />
                                <Button size="sm" variant="ghost" onClick={() => saveQuartersCount(g.id)} className="text-emerald-400 hover:text-emerald-300 h-6 w-6 p-0" data-testid={`save-quarters-${g.id}`}>
                                  <Check className="w-3 h-3" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingQuartersGameId(null)} className="text-slate-400 hover:text-slate-300 h-6 w-6 p-0">
                                  <X className="w-3 h-3" />
                                </Button>
                              </span>
                            ) : (
                              <span
                                className="cursor-pointer hover:text-amber-400 transition-colors"
                                onClick={() => { setEditingQuartersGameId(g.id); setEditingQuartersValue(g.quarters_count || 8); }}
                                title="Click to edit quarters"
                                data-testid={`quarters-display-${g.id}`}
                              >
                                Q{g.current_quarter}/{g.quarters_count}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              g.status === "active" ? "bg-emerald-900/50 text-emerald-300" :
                              g.status === "completed" ? "bg-slate-700 text-slate-300" :
                              "bg-amber-900/50 text-amber-300"
                            }`}>{g.status}</span>
                          </td>
                          <td className="py-3 px-2 text-right flex items-center justify-end gap-1">
                            {g.status !== "completed" && (
                              <Button size="sm" variant="ghost" onClick={() => openEditSchedule(g)} className="text-amber-400 hover:text-amber-300 hover:bg-amber-900/20 h-8 w-8 p-0" data-testid={`schedule-game-${g.id}`} title="Edit schedule">
                                <Calendar className="w-4 h-4" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => navigate(`/instructor/game/${g.id}`)} className="text-sky-400 hover:text-sky-300 hover:bg-sky-900/20 h-8 w-8 p-0" data-testid={`manage-game-${g.id}`} title="Manage game">
                              <ExternalLink className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => deleteGame(g.id, g.name)} className="text-red-400 hover:text-red-300 hover:bg-red-900/20 h-8 w-8 p-0" data-testid={`delete-game-${g.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {games.length === 0 && <p className="text-slate-500 text-center py-8">{t("admin.noGames")}</p>}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Schedule Edit Modal */}
      {editScheduleGameId && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setEditScheduleGameId(null)}>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 max-w-lg w-full mx-4 shadow-2xl max-h-[80vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-semibold text-lg mb-1 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-500" /> Edit Quarter Schedule
            </h3>
            <p className="text-slate-400 text-sm mb-4">Set date & time for each quarter to auto-advance</p>

            <div className="mb-4">
              <Label className="text-slate-300 text-xs">Timezone</Label>
              <Select value={editTz} onValueChange={setEditTz}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white text-sm" data-testid="edit-schedule-tz"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map(tz => <SelectItem key={tz} value={tz}>{tz}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2 mb-6">
              {editSchedule.map((qs, i) => (
                <div key={qs.quarter} className={`flex items-center gap-3 ${qs.advanced ? "opacity-50" : ""}`}>
                  <span className={`text-xs font-mono w-8 shrink-0 ${qs.advanced ? "text-emerald-400" : "text-slate-400"}`}>
                    Q{qs.quarter}
                  </span>
                  <input
                    type="datetime-local"
                    value={toLocalInput(qs.scheduled_at)}
                    onChange={e => updateEditScheduleItem(i, e.target.value)}
                    disabled={qs.advanced}
                    className="flex-1 bg-slate-700 border border-slate-600 text-white text-sm rounded px-3 py-2 disabled:opacity-40"
                    data-testid={`edit-schedule-q${qs.quarter}`}
                  />
                  {qs.advanced && <Check className="w-4 h-4 text-emerald-400 shrink-0" />}
                </div>
              ))}
            </div>

            <div className="flex gap-3 justify-end">
              <Button variant="ghost" onClick={() => setEditScheduleGameId(null)} className="text-slate-300 hover:text-white">
                Cancel
              </Button>
              <Button onClick={saveSchedule} disabled={savingSchedule} className="bg-amber-600 hover:bg-amber-700 gap-2" data-testid="save-schedule-btn">
                <Calendar className="w-4 h-4" /> {savingSchedule ? "Saving..." : "Save Schedule"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setConfirmAction(null)}>
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-semibold text-lg mb-2">{t("admin.confirmTitle")}</h3>
            <p className="text-slate-300 text-sm mb-6">
              {confirmAction.type === "resetPassword"
                ? `${t("admin.confirmReset")} ${confirmAction.label}?`
                : `${t("admin.confirmDelete")} "${confirmAction.label}"?`}
            </p>
            <div className="flex gap-3 justify-end">
              <Button variant="ghost" onClick={() => setConfirmAction(null)} className="text-slate-300 hover:text-white" data-testid="confirm-cancel-btn">
                {t("common.cancel")}
              </Button>
              <Button onClick={executeConfirm} className={confirmAction.type === "resetPassword" ? "bg-amber-600 hover:bg-amber-700" : "bg-red-600 hover:bg-red-700"} data-testid="confirm-action-btn">
                {confirmAction.type === "resetPassword" ? t("admin.resetPassword") : t("admin.confirmDeleteBtn")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
